import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Edit2, XCircle, CheckCircle, Search,
  UserCheck, Briefcase, Mail, Phone, Calendar, Eye, EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

const getAdminToken = () => {
  const authState = localStorage.getItem('bpv_admin_auth_state');
  if (!authState) throw new Error('Please login again');
  const { session } = JSON.parse(authState);
  if (!session?.access_token) throw new Error('Please login again');
  return session.access_token;
};

const EMPTY_FORM = {
  full_name: '',
  login_email: '',
  phone: '',
  address: '',
  role_id: '',
  joining_date: new Date().toISOString().split('T')[0],
  password: '',
};

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchStaff();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = getAdminToken();
      const res = await fetch(`${API_URL}/api/admin/staff/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) setRoles(result.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const token = getAdminToken();
      let url = `${API_URL}/api/admin/staff`;
      const params = [];
      if (filterRole !== 'all') params.push(`role_code=${filterRole}`);
      if (search.trim()) params.push(`search=${encodeURIComponent(search.trim())}`);
      if (params.length) url += '?' + params.join('&');

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) setStaff(result.staff || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filter/search changes
  useEffect(() => {
    fetchStaff();
  }, [filterRole]);

  const validate = () => {
    const errs = {};
    if (!formData.full_name.trim()) errs.full_name = 'Full name is required';
    if (!formData.login_email.trim()) errs.login_email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.login_email))
      errs.login_email = 'Invalid email format';
    if (!formData.role_id) errs.role_id = 'Role is required';
    if (!editingStaff) {
      if (!formData.password) errs.password = 'Password is required';
      else if (formData.password.length < 8)
        errs.password = 'Password must be at least 8 characters';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const token = getAdminToken();
      const url = editingStaff
        ? `${API_URL}/api/admin/staff/${editingStaff.id}`
        : `${API_URL}/api/admin/staff`;
      const method = editingStaff ? 'PUT' : 'POST';

      const body = editingStaff
        ? {
            full_name: formData.full_name,
            phone: formData.phone,
            role_id: formData.role_id,
            joining_date: formData.joining_date,
          }
        : formData;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const result = await res.json();

      if (result.success) {
        setSuccessMessage(result.message);
        resetForm();
        fetchStaff();
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        setErrors({ general: result.message || 'Something went wrong' });
      }
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (member) => {
    if (!window.confirm(`Are you sure you want to delete ${member.full_name}? This cannot be undone.`)) return;

    try {
      const token = getAdminToken();
      const res = await fetch(`${API_URL}/api/admin/staff/${member.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) { setSuccessMessage(result.message); fetchStaff(); }
      else alert(result.message);
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      full_name: member.full_name,
      login_email: member.login_email,
      phone: member.phone || '',
      address: member.address || '',
      role_id: member.role?.id || '',
      joining_date: member.joining_date || '',
      password: '',
    });
    setErrors({});
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setEditingStaff(null);
    setShowForm(false);
    setShowPassword(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    const pwd = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setFormData((prev) => ({ ...prev, password: pwd }));
    setShowPassword(true);
  };

  const filteredStaff = staff.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.full_name?.toLowerCase().includes(q) ||
      m.login_email?.toLowerCase().includes(q) ||
      m.employee_id?.toLowerCase().includes(q)
    );
  });

  const getRoleBadgeColor = (roleCode) => {
    const colors = {
      WMG: 'bg-blue-100 text-blue-800',
      MNG: 'bg-purple-100 text-purple-800',
      HR: 'bg-pink-100 text-pink-800',
      ACT: 'bg-yellow-100 text-yellow-800',
      OPS: 'bg-orange-100 text-orange-800',
      CSP: 'bg-green-100 text-green-800',
    };
    return colors[roleCode] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-orange-600" />
            Staff Management
          </h1>
          <p className="text-gray-600 mt-1">Create and manage staff members and their roles</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Staff
          </button>
        )}
      </div>

      {/* Success */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-5">
            {editingStaff ? `Edit: ${editingStaff.full_name}` : 'Create New Staff Member'}
          </h2>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="John Doe"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.full_name ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Login Email <span className="text-red-500">*</span>
              </label>
              <input
                name="login_email"
                type="email"
                value={formData.login_email}
                onChange={handleChange}
                disabled={!!editingStaff}
                placeholder="john@brightplanet.com"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  editingStaff ? 'bg-gray-50 cursor-not-allowed' : ''
                } ${errors.login_email ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.login_email && <p className="text-red-500 text-xs mt-1">{errors.login_email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="9876543210"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main Street, City"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Role */}            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.role_id ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select Role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.role_name} ({r.role_code})
                  </option>
                ))}
              </select>
              {errors.role_id && <p className="text-red-500 text-xs mt-1">{errors.role_id}</p>}
              {formData.role_id && (
                <p className="text-xs text-gray-500 mt-1">
                  Employee ID prefix:{' '}
                  <span className="font-semibold text-orange-600">
                    {roles.find((r) => r.id === formData.role_id)?.employee_prefix || '...'}
                  </span>
                  001, 002...
                </p>
              )}
            </div>

            {/* Joining Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Joining Date</label>
              <input
                type="date"
                name="joining_date"
                value={formData.joining_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Password (create only) */}
            {!editingStaff && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Min 8 characters"
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.password ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-3 py-2 text-xs bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>
            )}

            {/* Actions */}
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Saving...' : editingStaff ? 'Update Staff' : 'Create Staff'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchStaff()}
            placeholder="Search by name, email, ID..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
        >
          <option value="all">All Roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.role_code}>
              {r.role_name}
            </option>
          ))}
        </select>
        <button
          onClick={fetchStaff}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          Search
        </button>
        <span className="text-sm text-gray-500 ml-auto">
          {filteredStaff.length} staff member{filteredStaff.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-gray-500">Loading staff...</p>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No staff members found</h3>
            <p className="text-gray-600 mb-4">
              {search || filterRole !== 'all'
                ? 'No staff match your filters'
                : 'Create your first staff member to get started'}
            </p>
            {!showForm && filterRole === 'all' && !search && (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                <Plus className="w-5 h-5" />
                Add First Staff
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStaff.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-orange-600">
                          {member.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{member.full_name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Briefcase size={10} />
                          {member.employee_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm text-gray-700 flex items-center gap-1">
                        <Mail size={12} className="text-gray-400" />
                        {member.login_email}
                      </div>
                      {member.phone && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone size={12} className="text-gray-400" />
                          {member.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(member.role?.role_code)}`}>
                      <UserCheck size={11} />
                      {member.role?.role_name || '—'}
                    </span>
                    {member.status?.status_name && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 mt-1">
                        {member.status.status_name}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {member.joining_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(member.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(member)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={15} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(member)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <XCircle size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StaffManagement;
